import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Button, Input } from "@akuna/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useCreateServiceGroup, type ServiceGroupRow } from "@/hooks/useServiceGroups";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGroupCreated?: (group: ServiceGroupRow) => void;
};

export function CreateServiceGroupDialog({ open, onOpenChange, onGroupCreated }: Props) {
  const { t } = useTranslation("erp");
  const create = useCreateServiceGroup();
  const schema = z.object({
    name: z.string().trim().min(1, t("itemForm.serviceGroupDialog.nameRequired")).max(60),
  });
  const form = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema), defaultValues: { name: "" } });

  useEffect(() => {
    if (!open) form.reset({ name: "" });
  }, [open, form]);

  const onSubmit = form.handleSubmit(async ({ name }) => {
    const group = await create.mutateAsync({ name });
    onGroupCreated?.(group);
    onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("itemForm.serviceGroupDialog.title")}</DialogTitle>
          <DialogDescription>{t("itemForm.serviceGroupDialog.description")}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("itemForm.serviceGroupDialog.nameLabel")}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t("itemForm.serviceGroupDialog.namePlaceholder")} autoFocus />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("itemForm.serviceGroupDialog.cancel")}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {t("itemForm.serviceGroupDialog.save")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
